import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { PageHero, ACCENT, ACCENT_SOURDE } from "@/components/PageHero";
import { decalage } from "@/components/motion/Choregraphie";
import { trouverVille } from "@/data/villes";
import { schemaVille } from "./schema-ville";
import { PROJETS } from "@/data/projets";
import { usePosts } from "@/hooks/useBlog";
import NotFound from "@/pages/NotFound";
import { articlesForPage } from "@/data/editorial-links";
import { SeoSocial } from "@/components/SeoSocial";

const SITE = "https://tokamdarius.ca";

/** Prefixe de l'URL publique : /services/creation-site-web-<slug>.
    Exporte parce que l'aiguilleur de /services/:page s'en sert pour trancher
    entre une page ville et une page service : une seule declaration, sinon
    les deux fichiers derivent et une ville finit en 404. */
export const PREFIXE_VILLE = "creation-site-web-";

const PageVille = () => {
  /**
   * React Router 6 ne reconnait un parametre que sur un segment entier :
   * "creation-site-web-:ville" serait compare a la lettre et ne matcherait
   * jamais. La route capture donc tout le segment et le prefixe se retire ici.
   */
  const { page } = useParams();
  const slug = page?.startsWith(PREFIXE_VILLE)
    ? page.slice(PREFIXE_VILLE.length)
    : undefined;
  const ville = trouverVille(slug ?? "");

  /**
   * Les articles du blog, pour la section preuves. Appele avant le retour
   * anticipe : un hook ne se declare pas sous condition.
   *
   * Il n'y a pas de champ d'articles dans le modele de donnees des villes,
   * la liste vient donc du cache React Query. Au pre-rendu, la tache 3
   * seme la cle ["posts"] et les liens partent dans le HTML statique, ce
   * qui est tout l'objet de la manoeuvre. Cache vide, la section se rend
   * sans articles plutot que d'afficher un etat de chargement fige.
   */
  const { data: articles = [] } = usePosts();

  /**
   * Le vrai 404 du site plutot qu'un message local : lui seul pose le
   * noindex. Sans ca, chaque slug de ville invente deviendrait un soft 404
   * indexable, exactement ce que ce chantier cherche a eviter.
   */
  if (!ville) {
    return (
      <NotFound
        titre="Page introuvable"
        message="Cette ville n'a pas de page dédiée. La page des services porte la liste complète."
      />
    );
  }

  const url = `${SITE}/services/creation-site-web-${ville.slug}`;

  /** Les fiches reelles derriere les slugs choisis pour cette ville. */
  const projets = ville.projetsMisEnAvant.flatMap(
    (slugProjet) => PROJETS.find((p) => p.slug === slugProjet) ?? []
  );

  const articlesLies = articlesForPage(articles, ville.slug, "ville");

  /**
   * Les villes voisines resolues en fiches. Deux raisons de passer par la
   * donnee plutot que par le slug : l'ancre doit porter le nom ecrit de la
   * ville, accents et traits d'union compris, et une voisine pas encore
   * redigee se retire au lieu de pointer vers un 404.
   */
  const voisines = ville.villesVoisines.flatMap(
    (slugVoisine) => trouverVille(slugVoisine) ?? []
  );

  return (
    <div className="relative z-10 min-h-screen">
      <Header />
      <SeoSocial title={ville.meta.title} description={ville.meta.description} url={url} />

      <Helmet>
        <title>{ville.meta.title}</title>
        <meta name="description" content={ville.meta.description} />
        <link rel="canonical" href={url} />
        {/* Service avec areaServed, FAQPage et fil d'Ariane. Jamais
            LocalBusiness : quinze adresses inventees seraient du spam local. */}
        <script type="application/ld+json">{JSON.stringify(schemaVille(ville))}</script>
      </Helmet>

      <main className="pb-16">
        <PageHero
          surtitre="Création de site web"
          titre={
            <>
              Création de site web<br />
              <span style={{ color: ACCENT }}>à {ville.nom}</span>
            </>
          }
          soustitre={ville.redaction.accroche}
          actions={
            <>
              <Link to="/#contact">
                <Button
                  size="lg"
                  className="h-12 rounded-full px-7 text-[0.95rem] font-medium text-white"
                  style={{ background: ACCENT }}
                >
                  Discuter de votre projet
                </Button>
              </Link>
              <Link to="/projets">
                {/* Variante ghost plutot que outline : outline traine un
                    backdrop-blur-sm, interdit ici. Le reste des classes donne
                    le meme bouton que sur /services, sans le flou. */}
                <Button
                  size="lg"
                  variant="ghost"
                  className="h-12 rounded-full border border-white/20 bg-transparent px-7 text-[0.95rem] font-medium text-white hover:bg-white/10 hover:text-white"
                >
                  Voir mes réalisations
                </Button>
              </Link>
            </>
          }
        />

        {/* Reponse extractible. Premier bloc de contenu, 40 a 60 mots, sans
            argumentaire : c'est ce paragraphe que les moteurs generatifs
            decoupent pour citer. */}
        <section className="container mx-auto px-6">
          <div
            data-reveal
            className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-white/[0.04] p-8 sm:p-10"
          >
            <p className="text-lg leading-relaxed text-white/80">
              {ville.redaction.reponseExtractible}
            </p>
          </div>
        </section>

        <section className="relative overflow-hidden px-6 py-20">
          {/* Decor fixe. Pas de GlassDecor, pas de derive en boucle : une
              boucle infinie occupe le compositeur pour toujours, et le LCP
              mobile est deja a 3,9 s. Le mouvement de la page vient des
              entrees au defilement, qui ne se jouent qu'une fois. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background: `radial-gradient(60% 40% at 85% 12%, ${ACCENT_SOURDE} 0%, transparent 70%)`,
              opacity: 0.16,
            }}
          />

          <div className="container relative mx-auto max-w-3xl">
            {/* Le titre entre le premier, le paragraphe le suit d'un cran.
                C'est l'ordre de lecture rendu visible : rien ne bouge tant que
                le bloc est hors du champ, et il s'ecrit ensuite dans le sens
                ou on le lit. */}
            <h2 data-reveal className="text-3xl font-bold md:text-4xl">
              Le marché web à {ville.nom}
            </h2>
            <p
              data-reveal
              style={decalage(1)}
              className="mt-6 text-base leading-relaxed text-white/70"
            >
              {ville.redaction.marcheLocal}
            </p>

            <h2 data-reveal className="mt-16 text-3xl font-bold md:text-4xl">
              {ville.secteurPhare.nom}
            </h2>
            <p
              data-reveal
              style={decalage(1)}
              className="mt-6 text-base leading-relaxed text-white/70"
            >
              {ville.secteurPhare.pourquoi}
            </p>
            {/* La cascade se pose sur les puces, pas sur la liste : une liste
                marquee en bloc entre d'un coup, et on perd le rythme qui fait
                lire les besoins un par un. */}
            <ul className="mt-6 space-y-3 text-white/70">
              {ville.secteurPhare.besoinsWeb.map((besoin, index) => (
                <li
                  key={besoin}
                  data-reveal
                  style={decalage(index)}
                  className="flex gap-3"
                >
                  {/* Meme pastille ronde que le surtitre de PageHero. */}
                  <span
                    aria-hidden
                    className="mt-[0.6rem] h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: ACCENT }}
                  />
                  <span>{besoin}</span>
                </li>
              ))}
            </ul>

            {/* Preuves.
                ECART AVEC LE PLAN, assume et signale ici. La structure
                imposee a l'etape 3 de la tache 2 nomme huit sections, dont
                celle-ci, mais le bloc de code de cette meme etape l'oublie.
                Suivre le code a la lettre donnait une page sans un seul lien
                vers une fiche projet ni vers un article, alors que la section
                10 de la spec fixe le contenu, deux ou trois projets et un ou
                deux articles, et son effet recherche : donner aux fiches
                projets le chemin de crawl qui leur manque aujourd'hui.
                Le champ projetsMisEnAvant existait deja, et le contrat de la
                tache 1 verifie ses slugs, sans que rien ne les rende. */}
            <h2 data-reveal className="mt-16 text-3xl font-bold md:text-4xl">
              Ce que j'ai construit
            </h2>
            <p
              data-reveal
              style={decalage(1)}
              className="mt-6 text-base leading-relaxed text-white/70"
            >
              Des sites en ligne, que vous pouvez ouvrir et juger vous-même.
              J'ai écrit le code de chacun, du premier écran à la mise en
              production.
            </p>
            <ul className="mt-6 space-y-4">
              {projets.map((projet, index) => (
                <li
                  key={projet.slug}
                  /* La meme chute que les cartes de /projets : elles tombent
                     du haut, penchees, et se posent en rebondissant.
                     L'inclinaison alterne, sinon deux cartes qui tombent
                     exactement pareil se lisent comme un rideau qui descend.
                     La chute est portee par le li et le survol par le lien :
                     les deux ne se disputent jamais le meme transform. */
                  data-reveal="chute"
                  style={{
                    ["--rev-delai" as string]: `${Math.min(index, 5) * 110}ms`,
                    ["--rev-rot" as string]: index % 2 === 0 ? "-1.6deg" : "1.6deg",
                  }}
                >
                  <Link
                    to={`/projets/${projet.slug}`}
                    className="block rounded-3xl border border-white/10 bg-white/[0.04] p-6 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-1 hover:border-white/25 hover:bg-white/[0.07]"
                  >
                    <h3 className="text-lg font-semibold text-white">{projet.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">
                      {projet.tagline}
                    </p>
                    <p className="mt-3 text-xs text-white/40">{projet.stack.join(", ")}</p>
                  </Link>
                </li>
              ))}
            </ul>
            <p data-reveal className="mt-6 text-white/60">
              Le reste est sur la page des{" "}
              <Link
                to="/projets"
                className="underline underline-offset-4 decoration-white/30 transition-colors hover:text-white hover:decoration-white"
              >
                réalisations
              </Link>
              .
            </p>

            {articlesLies.length > 0 && (
              <div className="mt-8">
                <p data-reveal className="text-base leading-relaxed text-white/70">
                  Lectures du journal pour préparer votre site :
                </p>
                <ul className="mt-3 space-y-2 text-white/70">
                  {articlesLies.map((article, index) => (
                    <li key={article.slug} data-reveal style={decalage(index + 1)}>
                      <Link
                        to={`/blog/${article.slug}`}
                        className="underline underline-offset-4 decoration-white/30 transition-colors hover:text-white hover:decoration-white"
                      >
                        {article.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <h2 data-reveal className="mt-16 text-3xl font-bold md:text-4xl">
              {ville.distanceKm === 0 ? "Comment je travaille" : "Travailler à distance"}
            </h2>
            <p
              data-reveal
              style={decalage(1)}
              className="mt-6 text-base leading-relaxed text-white/70"
            >
              {ville.redaction.travailADistance}
            </p>

            <h2 data-reveal className="mt-16 text-3xl font-bold md:text-4xl">
              Questions fréquentes
            </h2>
            {/* Chaque question entre pour elle-meme, sans decalage entre
                elles. Une question fait six lignes en large et quatorze sur
                telephone, et les blocs sont espaces de 32 px : deux questions
                voisines ne sont jamais a l'ecran en meme temps. Le decalage
                n'aurait donc rien a decaler, il retarderait l'apparition d'un
                bloc deja arrive, et une entree qu'on ne voit pas jouer se lit
                comme de la lenteur. Le decalage reste ou les elements entrent
                ensemble : les puces d'une ligne, plus haut. */}
            <dl className="mt-6 space-y-8">
              {ville.faq.map((item) => (
                <div key={item.question} data-reveal>
                  <dt className="text-lg font-semibold text-white">{item.question}</dt>
                  <dd className="mt-3 text-base leading-relaxed text-white/70">
                    {item.reponse}
                  </dd>
                </div>
              ))}
            </dl>

            {ville.aidesNumeriques.length > 0 && (
              <>
                <h2 data-reveal className="mt-16 text-3xl font-bold md:text-4xl">
                  Aides au numérique applicables
                </h2>
                <ul className="mt-6 space-y-3 text-white/70">
                  {ville.aidesNumeriques.map((aide, index) => (
                    <li key={aide.nom} data-reveal style={decalage(index)}>
                      <a
                        href={aide.url}
                        rel="noopener"
                        className="underline underline-offset-4 decoration-white/30 transition-colors hover:text-white hover:decoration-white"
                      >
                        {aide.nom}
                      </a>{" "}
                      <span className="text-white/50">({aide.organisme})</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <p data-reveal className="mt-16 text-white/60">
              {voisines.length > 0 && (
                <>
                  Voir aussi :{" "}
                  {voisines.map((voisine, index) => (
                    <span key={voisine.slug}>
                      {index > 0 && ", "}
                      <Link
                        to={`/services/creation-site-web-${voisine.slug}`}
                        className="underline underline-offset-4 decoration-white/30 transition-colors hover:text-white hover:decoration-white"
                      >
                        {voisine.nom}
                      </Link>
                    </span>
                  ))}
                  .{" "}
                </>
              )}
              Tous mes{" "}
              <Link
                to="/services"
                className="underline underline-offset-4 decoration-white/30 transition-colors hover:text-white hover:decoration-white"
              >
                services
              </Link>
              .
            </p>
          </div>
        </section>

        <section className="relative overflow-hidden">
          {/* Meme lueur que la cloture de /services, mais figee : la classe
              decor-derive et sa duree d'animation sont retirees. */}
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div
              className="absolute"
              style={{
                top: "-45%",
                left: "8%",
                width: "48vw",
                height: "48vw",
                background: `radial-gradient(circle, ${ACCENT_SOURDE} 0%, transparent 62%)`,
                opacity: 0.22,
                filter: "blur(100px)",
              }}
            />
          </div>
          <div className="container relative mx-auto px-6 py-20 sm:py-28">
            <div
              data-reveal
              className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 sm:p-10"
            >
              <h2 className="text-[clamp(1.8rem,3.2vw,2.4rem)] font-bold leading-tight tracking-tight text-white">
                Parlons de votre projet à {ville.nom}
              </h2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-white/60">
                {ville.distanceKm === 0 ? (
                  <>
                    Je suis à {ville.nom}. On peut se rencontrer, ou tout faire à
                    distance si c'est plus simple pour vous.
                  </>
                ) : (
                  <>
                    Je travaille depuis Jonquière, à {ville.distanceKm} km de{" "}
                    {ville.nom}. Tout se fait à distance, et ça fonctionne : trois de
                    mes clients sont au Cameroun et en France.
                  </>
                )}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/#contact">
                  <Button
                    size="lg"
                    className="h-12 rounded-full px-7 text-[0.95rem] font-medium text-white transition-transform hover:scale-[1.02]"
                    style={{ background: ACCENT, boxShadow: `0 12px 40px -12px ${ACCENT}` }}
                  >
                    Me contacter
                  </Button>
                </Link>
                <a
                  href="mailto:tokamdarius@gmail.com"
                  className="text-[0.95rem] font-medium text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline"
                >
                  tokamdarius@gmail.com
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PageVille;
